/**
 * Which sidebar destinations this tenant's purchased services unlock.
 *
 * One account can hold website support, the phone receptionist, or both, sold
 * on two separate Stripe subscriptions (see `src/billing/entitlements.py`'s
 * `resolve_services`, whose `services` shape this module reads verbatim).
 * `AppShell` is the only caller: it filters each nav group's items by this
 * result and drops a group that ends up empty.
 *
 * ROUTES ARE UNCHANGED. This gates the SIDEBAR only -- a direct URL still
 * renders whichever page it always rendered. Route-level guards are a later,
 * separate piece of work.
 *
 * `null` means "show everything" -- both for a missing/malformed `services`
 * (fail open, exactly like `plan_readable` already does for the feature
 * locks) and for the boundary rows of the truth table where nothing should
 * be hidden: an undecided trial that has never touched either service, and
 * both services active. A tenant with neither service currently active but
 * a receptionist relationship that ENDED is a narrower exception (I-F1-1,
 * batch-4 final review): see `visibleNavPaths`.
 *
 * A non-null result is the subset of the two GATED path lists
 * (`WEB_ONLY_PATHS`, `RECEPTIONIST_PATHS`) that should stay visible; every
 * other nav entry is shared and is never touched by this function at all.
 */

import { accountType } from "./goLiveChecklist.mjs";

export const WEB_ONLY_PATHS = [
  "/aria",
  "/assistant",
  "/appearance",
  "/engagement",
  "/playground",
  "/install",
  // Added by the W5 onboarding audit (I2/I20): these were wired into
  // AppShell.tsx by the premium-console merge AFTER this gating list was
  // written, so a phone-only (B) tenant saw them ungated. All three are
  // web-conversation surfaces with nothing for a receptionist-only tenant.
  "/quality",
  "/evaluation",
  "/knowledge-improvement",
];

export const RECEPTIONIST_PATHS = ["/receptionist", "/calls"];

/**
 * Destinations every account type keeps, whatever it bought. Never read by
 * `visibleNavPaths`/`filterNavGroups` (anything outside the two gated lists
 * is shared by construction); listed so a shared entry is a decision with a
 * test, not an accident. `/settings#business-profile` is here because the
 * phone-only go-live checklist links to it (review I7), and the business
 * profile feeds both services.
 */
export const SHARED_NAV_PATHS = [
  "/overview",
  "/inbox",
  "/analytics",
  "/settings#knowledge",
  "/settings#business-profile",
  "/settings#account",
];

function activeFlag(section) {
  return section && typeof section === "object" ? section.active : undefined;
}

export function visibleNavPaths(services) {
  if (!services || typeof services !== "object") return null;
  const web = activeFlag(services.web_support);
  const rec = activeFlag(services.receptionist);
  if (typeof web !== "boolean" || typeof rec !== "boolean") return null;
  if (web && rec) return null; // both bought -- show all
  if (!web && !rec) {
    // Neither service is active right now. An undecided trial (nothing ever
    // purchased) still needs the full nav to stay discoverable, but a B
    // tenant whose receptionist has ENDED never wanted the website, and the
    // website-flavoured full nav is a dead end for them (batch-4 final
    // review, I-F1-1). Mirrors accountType()'s own "B" classification in
    // goLiveChecklist.mjs exactly, so the two modules can never disagree
    // about who this tenant is.
    return accountType(services) === "B" ? new Set(RECEPTIONIST_PATHS) : null;
  }
  return new Set(web ? WEB_ONLY_PATHS : RECEPTIONIST_PATHS);
}
/** Every nav destination either of the two gated lists names. An item outside
 *  this set is shared and is never filtered by `visibleNavPaths`'s result.
 *  Exported (F1 fix round 1, I3) so `CommandPalette` can apply the exact
 *  same "unless shared" rule `filterNavGroups` does below, instead of its
 *  own narrower filter that dropped every shared route for a gated
 *  account type. */
export const GATED_NAV_PATHS = new Set([...WEB_ONLY_PATHS, ...RECEPTIONIST_PATHS]);

/**
 * Applies a `visibleNavPaths` result to a list of `{ label, items }` nav
 * groups, dropping any item the tenant's services don't unlock and then
 * dropping a group left with no items rather than rendering an empty label.
 * Shared destinations (outside both gated lists) always survive, regardless
 * of `visible`. A `null` `visible` (unknown, undecided trial, or both
 * services bought) shows every gated item too.
 *
 * Pure and side-effect free: `groups` and its items are never mutated, and a
 * new array/objects are returned so callers can rely on referential
 * inequality to detect a change.
 */
export function filterNavGroups(groups, visible) {
  return groups
    .map((g) => ({
      ...g,
      items: g.items.filter(
        (it) => visible === null || !GATED_NAV_PATHS.has(it.to) || visible.has(it.to),
      ),
    }))
    .filter((g) => g.items.length > 0);
}
